import { useState } from 'react';

import { useCreateApiKey, useCreateMcpToken } from '@/api/queries';
import { mcpTokenInNameMax } from '@/api/generated/schemas/mcpTokenIn.zod';
import { useSession } from '@/session/SessionContext';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { CopyButton } from '@/ui/CopyButton';
import { FieldLabel } from '@/ui/FieldLabel';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';
import { AlertTriangle } from 'lucide-react';

import { ScopeChecks } from './ScopeChecks';

import { ExpiryField, isDayInRange } from './ExpiryField';
import { DEFAULT_DAYS, defaultScopes, earliestDay, latestDay, SCOPES, utcDay } from './mcpScopes';

/** An MCP token for an AI tool, or an API key for an internal system: one dialog, the same rules, their own words. */
export type CredentialKind = 'token' | 'key';

export const CREDENTIAL_COPY = {
  token: {
    title: 'Create token',
    created: 'Token created',
    field: 'Your token',
    subtitle: 'An AI tool using this token reads Wynxx Discover as you, limited to what you choose.',
    nameHint: 'A label to recognize the token by, such as the tool or the machine it lives on.',
    placeholder: 'Claude Code on my laptop',
    use: 'To use it, choose How to connect on this page and paste it where the command says <your token>.',
  },
  key: {
    title: 'Create API key',
    created: 'API key created',
    field: 'Your API key',
    subtitle: 'An internal system using this key calls the integration API as you, limited to what you choose.',
    nameHint: 'A label to recognize the key by, such as the system or the job that uses it.',
    placeholder: 'ERP nightly sync',
    use: 'Send it as Authorization: Bearer <your key>. The API guide tab shows how.',
  },
} as const;

interface Created {
  name: string;
  secret: string;
}


/** The new token or key, shown once. It lives in the dialog's state only: closing the dialog ends it. */
function CreatedBody({ created, kind }: { created: Created; kind: CredentialKind }) {
  return (
    <div className="stack-md">
      <div className="alert alert-warning" role="alert">
        <AlertTriangle aria-hidden="true" />
        <div className="a-body">Shown once: copy it now. You won’t see it again, and you can’t recover it later.</div>
      </div>
      <div className="field">
        <label htmlFor="credential-secret">{CREDENTIAL_COPY[kind].field}</label>
        <div className="token-row">
          {/* Focus lands here: the form that had focus is gone, and the token is what to act on. */}
          <input
            id="credential-secret"
            className="mono-input"
            readOnly
            autoFocus
            autoComplete="off"
            spellCheck={false}
            value={created.secret}
            onFocus={(e) => e.currentTarget.select()}
          />
          <CopyButton text={created.secret} />
        </div>
      </div>
      <p className="hint">{CREDENTIAL_COPY[kind].use}</p>
    </div>
  );
}

export function CreateTokenDialog({ onClose, kind = 'token' }: { onClose: () => void; kind?: CredentialKind }) {
  const { can } = useSession();
  const createToken = useCreateMcpToken();
  const createKey = useCreateApiKey();
  const create = kind === 'key' ? createKey : createToken;
  const copy = CREDENTIAL_COPY[kind];
  const held = SCOPES.filter((scope) => can(scope.key));
  const [name, setName] = useState('');
  const [day, setDay] = useState(() => utcDay(DEFAULT_DAYS));
  const [chosen, setChosen] = useState<string[]>(() => defaultScopes(held));
  const [created, setCreated] = useState<Created | null>(null);

  // One Modal instance for both steps: a second one would hand focus back to the page behind it.
  if (created)
    return (
      <Modal
        title={copy.created}
        subtitle={created.name}
        onClose={onClose}
        footer={
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <CreatedBody created={created} kind={kind} />
      </Modal>
    );

  const [min, max] = [earliestDay(), latestDay()];
  const valid = name.trim().length > 0 && isDayInRange(day, min, max) && chosen.length > 0;
  const toggle = (key: string, on: boolean) => setChosen(on ? [...chosen, key] : chosen.filter((k) => k !== key));
  const submit = () =>
    valid &&
    create.mutate(
      { name: name.trim(), expires_on: day, scopes: held.filter((s) => chosen.includes(s.key)).map((s) => s.key) },
      {
        onSuccess: (answer) => {
          setCreated({ name: answer.name, secret: 'token' in answer ? answer.token : answer.key });
          create.reset();
        },
      },
    );

  return (
    <Modal
      title={copy.title}
      subtitle={copy.subtitle}
      size="narrow"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <ActionButton state={buttonState(create)} label={copy.title} pendingLabel="Creating…" doneLabel="Created" disabled={!valid} />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={create.error} />
        <div className="field">
          <FieldLabel htmlFor="credential-name" id="credential-name-hint" label="Name" hint={copy.nameHint} />
          <input id="credential-name" type="text" aria-describedby="credential-name-hint" required maxLength={mcpTokenInNameMax} autoFocus autoComplete="off" placeholder={copy.placeholder} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <ScopeChecks held={held} chosen={chosen} onToggle={toggle} />
        <ExpiryField
          id="credential-expires-on"
          label="Expires on"
          tip="Pick a day from tomorrow to 90 days from today, in UTC. It works through the end of that day; you can extend it later."
          value={day}
          min={min}
          max={max}
          error="Pick a date from tomorrow to 90 days from today."
          onChange={setDay}
        />
      </div>
    </Modal>
  );
}
